import {
  Fragment,
  cloneElement,
  useRef,
  useEffect,
  type ReactElement,
  type HTMLAttributes,
  type MouseEvent,
} from 'react';
import {
  Icon,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHeader,
  TableHeaderCell,
  TableRow,
  type SemanticCOLORS,
  type PopupProps,
  type TableCellProps,
} from 'semantic-ui-react';
import styled from 'styled-components';
import { Info } from '@phosphor-icons/react';
import { TablePagination } from './TablePagination';
import { AppTableRowPlaceholder } from './Placeholder';
import { CommonConstant } from '../utility/constants';
import { DarkPopup } from './Popup';
import type { IPageSizeOption, TSortDirection } from 'types';
import type { ITableColumn } from 'types/common/table';

// Assuming CommonConstant.sortDirection maps to these strings
export interface ISortState {
  [key: string]: TSortDirection;
}

interface ICustomProps {
  [key: string]: any;
}

interface IAppTableProps<T = any> {
  AdditionalColumnRowComponent?: ReactElement;
  HeaderRowComponent?: ReactElement;
  RowComponent: ReactElement<{ item: T; index: number }>; // A RowComponent must accept 'item' and 'index' props
  columns: ITableColumn[];
  currentPage?: number;
  customPageSizes?: IPageSizeOption[];
  customProps?: ICustomProps;
  data: T[];
  enableScrollToTop?: boolean;
  footerComponent?: ReactElement;
  headerOffSet?: number;
  isMobile?: boolean;
  keyField?: keyof T | string; // keyField can be a key of T or a string if T is not fully defined
  loading?: boolean;
  loadingRows?: number;
  noDataMessage?: string;
  noShadow?: boolean;
  pageChanged?: (page: number) => void;
  pageSize?: number | string;
  pageSizeChanged?: (size: number) => void;
  showHeader?: boolean;
  sort?: ISortState;
  sortChanged?: (newSortState: ISortState, key: string) => void;
  structured?: boolean;
  textAlign?: TableCellProps['textAlign'];
  totalData?: number;
  totalPages?: number;
  wrapperClass?: string;
  noMarginTop?: boolean;
}

interface ITableHeaderToolTipProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
  helpText?: string;
  helpPosition?: PopupProps['position'];
  contentWidth?: number;
  color?: SemanticCOLORS | string;
}

interface ITableExpandCollapseProps {
  expand: boolean;
  clickHandler: (e: MouseEvent<HTMLButtonElement>, isExpanded: boolean) => void;
  text?: string;
  spaceFromLeft?: number;
}

interface ISortObject {
  [key: string]: 'asc' | 'desc';
}

interface IColumnProps {
  customProps?: Record<string, any>;
  column: ITableColumn;
  sort?: ISortObject;
  sortChanged: (key: string) => void;
  headerOffSet?: number;
}

// --- Styled Components ---

interface ITableContainerProps {
  noMarginTop?: boolean;
  noShadow?: boolean;
}

const TableContainer = styled.div<ITableContainerProps>`
  border-radius: 5px;
  margin-top: ${({ noMarginTop }) => (noMarginTop ? '0' : '1em')};
  max-width: 100%;
  ${({ noShadow }) =>
    !noShadow && 'box-shadow: 0 5px 25px rgba(57,57,72,0.05)!important;'}
`;

interface IShadowTableProps {
  headeroffset?: number;
}

export const ShadowTable = styled(Table)<IShadowTableProps>`
  thead tr:first-child > th {
    position: sticky !important;
    top: ${({ headeroffset }) => headeroffset}px;
    z-index: 2;
  }
  border: none !important;

  &.has-footer-table {
    margin-bottom: 0 !important;
  }

  &.sortable {
    thead {
      th {
        border-right: none !important;
        border-left: none !important;
      }
    }
  }

  td,
  th {
    word-break: break-word;
  }

  tr {
    &.action-hover {
      &:hover {
        .more-details {
          opacity: 1 !important;

          &:hover {
            background: ${({ theme }) => theme.colors.box};
            padding: 10px;
          }
        }
        .action-edit-button {
          opacity: 1;
        }
      }
      .action-cell {
        position: relative;
      }
    }
  }

  tr td {
    border-top: 1px solid #f6f6f6 !important;
  }

  tr th {
    border-bottom: 1px solid ${({ theme }) => theme.colors.black} !important;
  }

  tfoot tr th {
    border-bottom: none !important;
    border-top: 1px solid #f6f6f6 !important;
  }
`;

export const HoverTableRow = styled(TableRow)`
  transition: background 0.3s linear;
  &:hover {
    background: ${({ theme }) => theme.colors.box} !important;
    cursor: pointer;
  }
`;

export const ActionHoverTableRow = styled(TableRow)`
  transition: background 0.3s linear;
  &:hover {
    background: ${({ theme }) => theme.colors.box} !important;

    .more-details {
      opacity: 1 !important;
      &:hover {
        background: ${({ theme }) => theme.colors.box};
        padding: 10px;
      }
    }

    .action-container {
      opacity: 1 !important;
    }
  }
  .action-cell {
    position: relative;
  }
`;

export const BorderTableRow = styled(TableRow)`
  &:hover {
    .border {
      display: block;
    }
  }
`;

interface IBorderTableCellProps {
  expand?: 'true' | 'false';
}

export const BorderTableCell = styled(TableCell)<IBorderTableCellProps>`
  position: relative;

  .border {
    display: ${({ expand }) => (expand === 'true' ? 'block' : 'none')};
    background: ${({ theme }) => theme.colors.grey};
    width: 5px;
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
  }
`;

interface ICustomHeaderCellProps {
  headeroffset?: number;
  bgcolor?: string;
  textcolor?: string;
}

const CustomHeaderCell = styled(TableHeaderCell)<ICustomHeaderCellProps>`
  background: ${({ theme, bgcolor }) => bgcolor || theme.colors.white} !important;
  color: ${({ theme, textcolor }) => textcolor || theme.colors.grey} !important;
  font-size: 12px;
  line-height: 14px;
  font-weight: normal !important;
  position: sticky !important;
  top: ${({ headeroffset }) => headeroffset}px;
  z-index: 10 !important;

  &.wrapped-cell {
    vertical-align: bottom;
    text-align: center;
    min-width: 80px;
    span {
      transform: rotate(230deg);
      white-space: normal;
      word-break: keep-all;
    }
  }

  &.no-sort {
    cursor: default !important;
  }
`;

// --- Components ---

function Columns({
  column,
  sort,
  sortChanged,
  customProps = {},
  headerOffSet,
}: IColumnProps): ReactElement {
  return (
    <CustomHeaderCell
      width={column.width}
      textAlign={column.textAlign || 'center'}
      sorted={
        column.sortKey
          ? sort[column.sortKey] === CommonConstant.sortDirection.ascending
            ? 'ascending'
            : 'descending'
          : null
      }
      onClick={() => column.sortKey && sortChanged(column.sortKey)}
      className={`${column.sortKey ? '' : 'no-sort'} ${column.className || ''}`}
      style={column.style}
      bgcolor={column.bgcolor}
      textcolor={column.textcolor}
      headeroffset={headerOffSet}
    >
      {column.component
        ? cloneElement(column.component, {
            ...((column.component.props as Record<string, any>) || {}),
            ...(column.componentPropsKey
              ? (customProps[column.componentPropsKey] as Record<string, any>)
              : {}),
          })
        : column.text}
    </CustomHeaderCell>
  );
}

export function AppTable<T>({
  AdditionalColumnRowComponent,
  HeaderRowComponent,
  RowComponent,
  columns = [],
  currentPage = 0,
  customPageSizes,
  customProps = {},
  data = [],
  enableScrollToTop = false,
  footerComponent,
  headerOffSet = 0,
  isMobile,
  keyField = '_id',
  loading,
  loadingRows = 4,
  noDataMessage = 'No Data Found',
  noShadow,
  pageChanged,
  pageSize,
  pageSizeChanged,
  showHeader = true,
  sort,
  sortChanged,
  structured,
  textAlign = 'center',
  totalData = 0,
  totalPages,
  wrapperClass,
  noMarginTop,
}: IAppTableProps<T>): ReactElement {
  const myRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (myRef.current) {
      const yOffset = -80;
      const elementFromTop = myRef.current.getBoundingClientRect().top;
      const y = elementFromTop + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (enableScrollToTop) scrollToTop();
  }, [data, enableScrollToTop]);

  const orderChanged = (key: string) => {
    let direction: TSortDirection;
    if (!sort[key]) {
      const col = columns.find(x => x.sortKey === key);
      direction = (col?.firstDirection ||
        CommonConstant.sortDirection.ascending) as TSortDirection;
    } else {
      direction =
        sort[key] === CommonConstant.sortDirection.ascending
          ? (CommonConstant.sortDirection.descending as TSortDirection)
          : (CommonConstant.sortDirection.ascending as TSortDirection);
    }
    sortChanged({ [key]: direction }, key);
  };

  return (
    <>
      <TableContainer
        ref={myRef}
        noShadow={noShadow}
        className={wrapperClass}
        noMarginTop={noMarginTop}
      >
        <ShadowTable
          headeroffset={headerOffSet}
          structured={structured}
          sortable
          padded
          className={
            currentPage > 0 && totalData > 10
              ? `${isMobile ? 'd-block' : ''} has-footer-table`
              : ''
          }
        >
          {showHeader && (
            <TableHeader>
              {AdditionalColumnRowComponent &&
                cloneElement(AdditionalColumnRowComponent, {
                  ...((AdditionalColumnRowComponent &&
                    (AdditionalColumnRowComponent.props as any)) ||
                    {}),
                })}
              <TableRow textAlign={textAlign} verticalAlign="middle">
                {columns.map((item, index) => (
                  <Columns
                    headerOffSet={headerOffSet}
                    sort={sort}
                    customProps={customProps}
                    sortChanged={orderChanged}
                    key={`${item.key}-${index}`}
                    column={item}
                  />
                ))}
              </TableRow>
            </TableHeader>
          )}
          <TableBody className="table-body">
            {HeaderRowComponent &&
              cloneElement(HeaderRowComponent, {
                ...((HeaderRowComponent.props && (HeaderRowComponent.props as any)) ||
                  {}),
              })}
            {loading ? (
              <AppTableRowPlaceholder rows={loadingRows} colSpan={columns.length} />
            ) : (
              (data || []).map((item, index) => (
                <Fragment
                  key={String((item as any)?.[keyField as any] ?? `row-${index}`)}
                >
                  {cloneElement(RowComponent, {
                    ...RowComponent.props,
                    item,
                    index,
                  })}
                </Fragment>
              ))
            )}
          </TableBody>
          {!loading && (data || []).length === 0 && (
            <TableFooter fullWidth>
              <TableRow>
                <TableHeaderCell colSpan={columns.length}>
                  {noDataMessage}
                </TableHeaderCell>
              </TableRow>
            </TableFooter>
          )}
        </ShadowTable>
      </TableContainer>
      {(footerComponent || (currentPage > 0 && totalData > 10)) && (
        <TablePagination
          noPadding
          colSpan={columns.length}
          currentPage={currentPage}
          totalPages={totalPages}
          pageChanged={pageChanged}
          pageSizeChanged={pageSizeChanged}
          pageSize={pageSize}
          footerComponent={footerComponent}
          customPageSizes={customPageSizes}
          totalData={totalData}
        />
      )}
    </>
  );
}

const InfoIcon = styled(Info)`
  cursor: pointer;
  margin-left: 5px;
  margin-bottom: -2px;
`;

export function TableHeaderToolTip({
  label = '',
  helpText = '',
  helpPosition = 'top center',
  contentWidth,
  color,
  ...rest
}: ITableHeaderToolTipProps): ReactElement {
  return (
    <>
      {label}
      {helpText && (
        <DarkPopup
          contentWidth={contentWidth}
          position={helpPosition}
          trigger={<InfoIcon size={14} color={color} />}
          {...rest}
        >
          {helpText}
        </DarkPopup>
      )}
    </>
  );
}

const ExpandCollapseButtonBox = styled.div`
  display: inline-block;
`;

interface IExpandCollapseButtonProps {
  spaceFromLeft?: number;
}

const ExpandCollapseButton = styled.button<IExpandCollapseButtonProps>`
  display: flex;
  align-items: center;
  height: 100%;
  position: absolute;
  top: 0;
  z-index: 99;
  font-family: 'Nunito Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #393948;
  background: none;
  border: 0;
  cursor: pointer;
  left: ${({ spaceFromLeft }) => spaceFromLeft || 10}px;

  i {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: 5px;
  }
`;

export function TableExpandCollapse({
  expand,
  clickHandler,
  text = '',
  spaceFromLeft = 0,
}: ITableExpandCollapseProps): ReactElement {
  return (
    <ExpandCollapseButtonBox>
      {text}
      <ExpandCollapseButton
        spaceFromLeft={spaceFromLeft}
        onClick={e => clickHandler(e, expand)}
      >
        {expand ? 'Collapse All' : 'Expand All'}
        <Icon name={expand ? 'chevron down' : 'chevron right'} />
      </ExpandCollapseButton>
    </ExpandCollapseButtonBox>
  );
}
